import { ColumnDef } from "@tanstack/react-table";
import EstimateBadge from "../components/EstimateBadge";
import { Button } from "@/components/ui/button";
import { CombinedRow } from "@/types/itemsheet";
import { ItemSheetExtraTag } from "@/consts/Estimate";
import { cn } from "@/libs/utils";

declare module "@tanstack/react-table" {
  interface TableMeta<TData extends unknown> {
    updateData: (rowIndex: number, columnId: string, value: unknown) => void;
  }
}

export const createColumns = (
  handleRowSelect: (
    id: string,
    list_price?: string | number | null,
    unit_cost?: string | number | null
  ) => void
): ColumnDef<CombinedRow>[] => [
  {
    id: "sourceBadge",
    cell: ({ row }) => {
      if (row.original.isGoogleResult) {
        return <EstimateBadge type={ItemSheetExtraTag.GOOGLE_SEARCH} noLabel />;
      }

      if (row.original.isPurchaseHistory) {
        return <EstimateBadge type={ItemSheetExtraTag.PAST_PURCHASE} noLabel />;
      }

      return null;
    },
    size: 40,
  },
  {
    accessorKey: "product_name",
    header: "제품명",
    cell: ({ row }) => {
      return (
        <div className="flex items-center">
          <div className="mr-2">
            <span className="text-xs text-gray-A font-semibold">
              {row.original.product_name}
            </span>
          </div>
          <div className="relative">
            <Button
              size="sm"
              className="absolute left-0 top-1/2 transform -translate-y-1/2 hidden group-hover:flex bg-dark-red text-[10px] text-white font-semibold hover:bg-dark-red/80 rounded-sm"
              onClick={() => {
                handleRowSelect(
                  row.id,
                  row.original.list_price,
                  row.original.unit_cost
                );
              }}
            >
              데이터 적용하기
            </Button>
          </div>
        </div>
      );
    },
    size: 450,
  },
  {
    accessorKey: "product_id",
    header: "제품번호",
    cell: ({ row }) => {
      const { product_id, url } = row.original;

      const handleClick = () => {
        if (url) {
          window.open(url, "_blank");
        }
      };

      return (
        <div
          className={cn("flex items-center", url && "underline cursor-pointer")}
          onClick={handleClick}
        >
          <span className="text-xs text-gray-A font-semibold">
            {product_id}
          </span>
        </div>
      );
    },
    size: 170,
  },
  {
    accessorKey: "manufacturer",
    header: "제조사",
    size: 220,
  },
  {
    accessorKey: "vendor",
    header: "벤더",
    size: 175,
  },
  {
    accessorKey: "buyer",
    header: "구매업체",
    size: 175,
  },
  {
    accessorKey: "specification",
    header: "규격",
    size: 110,
  },
  {
    accessorKey: "unit",
    header: "단위",
    size: 100,
  },
  {
    accessorKey: "quantity",
    header: "수량",
    size: 100,
  },
  {
    accessorKey: "unit_cost",
    header: "매입가",
    size: 150,
  },
  {
    accessorKey: "list_price",
    header: "판매가",
    cell: ({ row, table }) => {
      return (
        <div className="flex items-center">
          <input
            type="number"
            className="w-full px-2 py-1 text-xs border rounded"
            value={row.original.list_price || ""}
            onChange={(e) => {
              const value = e.target.value;
              const numValue = value ? Number(value) : null;

              // Update the row data
              row.original.list_price = numValue;

              // Force table rerender
              table.options.meta?.updateData(row.index, "list_price", numValue);
            }}
            onFocus={(e) => e.target.select()}
          />
        </div>
      );
    },
    size: 150,
  },
  {
    id: "total_list_price",
    header: "판매가 합계",
    accessorFn: (row) =>
      row.quantity != null && row.list_price != null
        ? row.quantity * row.list_price
        : null,
    cell: ({ getValue }) => {
      const total = getValue() as number | null;
      return (
        <span className="text-xs text-gray-A font-semibold">
          {total != null ? total.toLocaleString() : ""}
        </span>
      );
    },
    size: 150,
  },
];
